import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { isOnline } from '@/lib/connectivity';
import { cacheAppSession, clearCachedAppSession, readCachedAppSession } from '@/lib/auth/session-cache';
import { resolveAuthSession } from '@/lib/auth/session-resolve';
import type { AppSession } from '@/lib/auth/session-types';

import {
  authClient,
  signIn as betterAuthSignIn,
  signOut as betterAuthSignOut,
  useSession as useBetterAuthSession,
} from './client';
import { clearNativeSessionToken } from './capacitor-session';
import { isNativePlatform } from './is-native';

export type { AppSession } from './session-types';

export function AuthProvider({ children }: { children: ReactNode }) {
  return children;
}

export { authClient, betterAuthSignIn as signIn };

export async function signOut(): Promise<void> {
  clearCachedAppSession();
  if (isNativePlatform()) {
    await clearNativeSessionToken();
  }
  await betterAuthSignOut();
}

function betterAuthUserToApp(user: {
  id: string;
  name: string;
  email: string;
  image?: string | null;
}): AppSession['user'] {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    image: user.image,
  };
}

export function useSession(): ReturnType<typeof useBetterAuthSession> {
  const cookieSession = useBetterAuthSession();

  const cookieData = cookieSession.data;
  useEffect(() => {
    if (!cookieData?.user) {
      return;
    }
    cacheAppSession(betterAuthUserToApp(cookieData.user));
  }, [cookieData?.user?.id, cookieData?.user?.name, cookieData?.user?.email]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const refresh = () => {
      if (!isOnline()) {
        return;
      }
      void cookieSession.refetch();
    };
    window.addEventListener('online', refresh);
    return () => window.removeEventListener('online', refresh);
  }, [cookieSession.refetch]);

  if (cookieSession.data?.user) {
    return cookieSession;
  }

  return resolveAuthSession(
    {
      data: null,
      isPending: cookieSession.isPending,
      error: cookieSession.error,
      refetch: cookieSession.refetch,
    },
    readCachedAppSession(),
  ) as ReturnType<typeof useBetterAuthSession>;
}
